// biome-ignore lint/style/useImportType: <explanation>

import { isAreaLayoutOnlyChange } from "./modelDiff";

import { sceneHasRenderableContent } from "./sceneHasContent";

import {
  Graphics,
  Container,
  ContainerChild,
  Sprite,
  Texture,
  Application,
  Text,
  Rectangle,
} from "pixi.js";

import type { Filter } from "pixi.js";

import { compositionConfig } from "./compositionConfig";

import { IBackgroundType } from "../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Theme.interface";

import StrategyBounds from "./StrategyBounds";

import { ITypeMedia } from "../../../interfaces/Artists";

import type { IStudioArea } from "../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Areas.interface";

import type { ISceneDB } from "../../../interfaces/Scenes";

import DrawCache from "./DrawCache";

// biome-ignore lint/style/useImportType: <explanation>

import PlayerFactory from "./PlayerFactory";

import { IStudio } from "../../../screens/Dashboard/SubComponents/Studio/interfaces/Studio.interface";

import BackgroundImageRenderer from "./Renders/BackgroundImageRenderer";

import { createPixiFilter } from "../../../components/Assembler/layouts/Resource/SubComponents/FilterModal/FilterModal.utils";

import { AVAILABLE } from "../../../components/Assembler/layouts/Resource/SubComponents/FilterModal/FilterModal.constants";

import type { FilterKind } from "../../../components/Assembler/layouts/Resource/SubComponents/FilterModal/FilterModal.types";

import WidgetsFactory from "./WidgetsFactory";

import ElementsFactory from "./ElementsFactory";

import AlertsFactory from "./AlertsFactory";

import OverlaysFactory from "./OverlaysFactory";

import PanelsFactory from "./PanelsFactory";

import CustomOverlayRenderer from "./Overlays/CustomOverlayRenderer";

import {
  UpdaterList,
  type OverlayFrameUpdater,
} from "./Overlays/OverlayElementAnimator";

import {
  MaskType,
  type MaskComposition,
} from "../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Mask.interface";

import { browserCaptureService } from "../../../services/BrowserCaptureService";

import BrowserPreviewManager from "../../Managers/BrowserPreviewManager";

class RenderFactory {
  private Scene: ISceneDB;

  private App: Application;

  // Container raíz de ESTA escena (hijo de App.stage). Todo lo de la escena se

  // dibuja acá, no en App.stage, para que el SceneCompositor pueda mantener

  // varias escenas vivas a la vez y swapear visibilidad / transicionar.

  private Root: Container;

  // Rect full-canvas con el color de fondo de la escena (antes era el global

  // App.renderer.background.color; per-escena para que las transiciones compongan

  // bien dos escenas con fondos distintos).

  private BgColor?: Graphics;

  // Estado de pausa del trabajo per-frame (escena oculta en el pool keep-alive).

  private Paused = false;

  private DrawCache: DrawCache;

  private Players: PlayerFactory;

  private TestPattern?: Graphics;

  private BackgroundImageRenderer: BackgroundImageRenderer;

  private WidgetsFactory: WidgetsFactory;

  private ElementsFactory: ElementsFactory;

  private AlertsFactory: AlertsFactory;

  private OverlaysFactory: OverlaysFactory;

  private PanelsFactory: PanelsFactory;

  // Renderer reusado para el mask-composition (mismo pipeline que area.overlay).

  private MaskOverlayRenderer: CustomOverlayRenderer;

  // Updaters per-frame de los elementos del mask (animación, shaders animados,

  // partículas, procedural, typewriter) + tiempo de inicio. Un único ticker

  // callback los recorre; se remueve en clear() para no leakear / degradar fps.

  private MaskAnim: {
    updaters: OverlayFrameUpdater[];

    startMs: number;
  } | null = null;

  private MaskAnimTicker: (() => void) | null = null;

  // Updaters que devolvieron true (entry-anim finita en su estado final,
  // typewriter completo): no se vuelven a llamar hasta que la capa se muestre
  // de nuevo (setVisible(true) reinicia el reloj y este set). Se CONSERVAN en
  // las listas para poder reproducirlos otra vez.

  private DoneUpdaters = new WeakSet<OverlayFrameUpdater>();

  // ¿El MaskAnimTicker está actualmente añadido al App.ticker? Evita doble-add

  // (ensureMaskAnimTicker en renderSelf + resume() del pool lo agregarían 2×).

  private MaskTickerActive = false;

  private ScreenshotTimer: NodeJS.Timeout | null = null;

  ThumbnailWorker!: Worker;

  ThumbnailQueueFree = true;

  private Options = {
    width: compositionConfig.width,

    height: compositionConfig.height,
  };

  private State = {
    isDrawing: true,

    onLive: false,
  };

  constructor(scene: ISceneDB, app: Application, players: PlayerFactory) {
    this.Scene = scene;

    this.App = app;

    this.Players = players;

    this.DrawCache = new DrawCache();

    // Root de la escena. sortableChildren para respetar zIndex dentro de la escena.

    this.Root = new Container();

    this.Root.sortableChildren = true;

    this.Root.label = `scene:${scene.model.id}`;

    app.stage.addChild(this.Root);

    this.BackgroundImageRenderer = new BackgroundImageRenderer(
      app,

      this.Options,

      this.Root,
    );

    this.WidgetsFactory = new WidgetsFactory(app, this.DrawCache, this.Root);

    this.ElementsFactory = new ElementsFactory(app, this.DrawCache, this.Root);

    this.AlertsFactory = new AlertsFactory(app, this.DrawCache, this.Root);

    this.OverlaysFactory = new OverlaysFactory(
      app,
      this.DrawCache,
      this.Root,
      () => this.ensureMaskAnimTicker(),
    );

    this.PanelsFactory = new PanelsFactory(
      app,
      this.DrawCache,
      this.Root,
      () => this.ensureMaskAnimTicker(),
    );

    this.MaskOverlayRenderer = new CustomOverlayRenderer();

    this.createWorker();
  }

  /** Container raíz de esta escena (lo monta/posiciona el SceneCompositor). */

  getRoot(): Container {
    return this.Root;
  }

  getSceneId(): string {
    return this.Scene.model.id;
  }

  /** Ids de las áreas de esta escena (para liberar sus fuentes al evictar). */

  getAreaIds(): string[] {
    return (this.Scene.model.areas ?? []).map((area) => area.id);
  }

  /** Muestra/oculta la escena completa. renderable:false → Pixi saltea su dibujo. */

  setVisible(visible: boolean): void {
    if (this.Root.destroyed) return;

    const wasVisible = this.Root.visible;

    this.Root.visible = visible;

    this.Root.renderable = visible;

    // Al pasar a visible arranca el reloj de los updaters: las entry-anims se
    // reproducen al mostrar la capa (no desde el build offscreen/prewarm) y las
    // partículas precalentadas siguen desde ahí (toleran el reinicio).
    if (visible && !wasVisible) {
      const now = Date.now();

      for (const state of this.animStates) {
        if (state) state.startMs = now;
      }

      this.DoneUpdaters = new WeakSet<OverlayFrameUpdater>();
    }

    // Los updaters per-frame (partículas, procedural, animaciones) solo corren
    // mientras la capa se ve: al ocultarla se retira el ticker y al mostrarla
    // se vuelve a registrar (si hay updaters y no está pausada).
    if (visible) {
      this.ensureMaskAnimTicker();
    } else {
      this.removeAnimTicker();
    }
  }

  private removeAnimTicker(): void {
    if (this.MaskAnimTicker && this.MaskTickerActive) {
      this.App.ticker?.remove(this.MaskAnimTicker);

      this.MaskTickerActive = false;
    }
  }

  /**
   * Pausa el trabajo per-frame de la escena oculta (regla de fps: una escena que
   * no se ve no debe consumir GPU/CPU). Quita el ticker del mask + pausa alertas.
   * El costo de dibujo ya se evita con setVisible(false)/renderable.
   */

  pause(): void {
    if (this.Paused) return;

    this.Paused = true;

    if (this.MaskAnimTicker && this.MaskTickerActive) {
      this.App.ticker?.remove(this.MaskAnimTicker);

      this.MaskTickerActive = false;
    }

    this.AlertsFactory.pause();
  }

  /** Reanuda el trabajo per-frame (la escena vuelve a ser visible). */

  resume(): void {
    this.State.isDrawing = true;

    if (!this.Paused) return;

    this.Paused = false;

    // Registra el ticker solo si la capa ya se ve; si sigue oculta (build
    // offscreen de un pase) lo hará setVisible(true).
    this.ensureMaskAnimTicker();

    this.AlertsFactory.resume();
  }

  private createWorker() {
    this.ThumbnailWorker = new Worker(
      new URL("./Workers/ThumbnailWorker.ts", import.meta.url),

      {
        type: "module",

        name: "thumbnails",
      },
    );
  }

  private renderBackgroundColor() {
    const model = this.Scene.model;

    if (model.theme?.background?.type !== IBackgroundType.color) {
      // La escena no define color de fondo → no pintamos rect; el negro global del

      // renderer hace de base, y en transiciones la escena queda transparente

      // (deja ver la otra), que es lo deseado.

      if (this.BgColor && !this.BgColor.destroyed) this.BgColor.visible = false;

      return;
    }

    // Color de fondo per-escena como rect full-canvas en el Root (z más bajo),

    // en vez del global App.renderer.background.color (que rompía transiciones

    // entre escenas con fondos distintos).

    if (!this.BgColor || this.BgColor.destroyed) {
      this.BgColor = new Graphics();

      this.BgColor.label = "scene-bg-color";

      this.BgColor.zIndex = -1001;

      this.Root.addChild(this.BgColor);
    }

    this.BgColor.visible = true;

    this.BgColor.clear();

    this.BgColor.rect(0, 0, this.Options.width, this.Options.height).fill(
      model.theme.background.color,
    );

    this.Root.sortDirty = true;
  }

  private renderBackgroundImage() {
    this.BackgroundImageRenderer.render(this.Scene);
  }

  renderBackground(): void {
    this.renderBackgroundColor();

    this.renderBackgroundImage();
  }

  private haveAreas() {
    return (this.Scene.model.areas?.length ?? 0) > 0;
  }

  private haveBackground() {
    return this.Scene.model.theme?.background !== undefined;
  }

  /**
   * Renderiza/actualiza el árbol completo de ESTA escena en su Root. Es la
   * secuencia que antes vivía en PixiFactory.drawFrame, ahora per-layer para que
   * el SceneCompositor pueda construir/refrescar una escena (activa o pre-warm)
   * sin tocar a las demás. Reusa el DrawCache por-área → barato si ya estaba viva.
   */

  async renderSelf(onLive: boolean): Promise<void> {
    // Escena REALMENTE vacía → barras de color, para que se note que no hay

    // nada que emitir. El test cuenta `studio.mask`: una escena de diseño

    // (Inicio / Pausa / Cierre) sin textos editables ni widgets tiene

    // `areas: []`, sin theme y sin elements — todo su contenido está en la

    // máscara. Sin contarla, esas escenas se pintaban con las barras debajo del

    // diseño y parecían rotas.

    if (!sceneHasRenderableContent(this.Scene.model)) {
      this.renderTestPattern();
    } else {
      this.clearTestPattern();
    }

    if (this.haveBackground()) this.renderBackground();

    if (this.haveAreas()) await this.renderAreas(this.Scene.model.areas);

    await this.renderOverlays();

    await this.renderPanels();

    await this.renderWidgets();

    await this.renderElements();

    await this.renderAlerts();

    await this.drawMask();

    if (onLive) this.updateOnLive(onLive);
  }

  renderTestPattern(): void {
    if (this.TestPattern) {
      if (!this.TestPattern.destroyed && !this.TestPattern.parent) {
        this.Root.addChild(this.TestPattern);
      }

      return;
    }

    const testPatternGraphics = new Graphics();

    testPatternGraphics.label = "test-pattern";

    const testColors = [
      0xffd1dc, 0xffdfba, 0xbae1ff, 0xbaffc9, 0xe0bbff, 0xffb3ba, 0xbabfff,

      0xe8e8e8,
    ];

    const barWidth = this.Options.width / testColors.length;

    testColors.forEach((color, index) => {
      testPatternGraphics

        .rect(index * barWidth, 0, barWidth, this.Options.height)

        .fill(color);
    });

    this.Root.addChild(testPatternGraphics);

    // Static graphics — cache as texture so PixiJS skips re-rendering each tick

    testPatternGraphics.cacheAsTexture(true);

    this.TestPattern = testPatternGraphics;
  }

  /**
   * Saca las barras del árbol cuando la escena deja de estar vacía. `renderSelf`
   * se re-ejecuta ante cada edición: sin esto, una escena que arrancó vacía
   * seguía mostrando las barras por debajo del contenido nuevo (el gráfico
   * quedaba en el Root para siempre). Se conserva la instancia — volver a
   * agregarla es más barato que reconstruir el Graphics.
   */

  private clearTestPattern(): void {
    const pattern = this.TestPattern;

    if (!pattern || pattern.destroyed || !pattern.parent) return;

    pattern.parent.removeChild(pattern);
  }

  private isAreaHidden(area: IStudioArea) {
    return area.editorState?.isHidden === true;
  }

  private setAreaContainersVisibility(
    areaId: string,

    mode: "primary" | "fallback" | "hidden",
  ) {
    const primaryContainer = this.DrawCache.Containers[areaId];

    const fallbackContainer = this.DrawCache.Containers[`fallback_${areaId}`];

    if (primaryContainer && !primaryContainer.destroyed) {
      primaryContainer.visible = mode === "primary";
    }

    if (fallbackContainer && !fallbackContainer.destroyed) {
      fallbackContainer.visible = mode === "fallback";
    }
  }

  async renderAreas(areas: IStudioArea[]) {
    for (const key in areas) {
      const area = areas[key];

      if (this.isAreaHidden(area)) {
        this.setAreaContainersVisibility(area.id, "hidden");

        continue;
      }

      let element = this.Players.get(area.id);

      if (
        element instanceof HTMLVideoElement &&
        !element.srcObject &&
        !element.currentSrc &&
        element.readyState === HTMLMediaElement.HAVE_NOTHING
      ) {
        element = null;
      }

      if (!element) {
        await this.renderFallback(area, this.App);

        this.setAreaContainersVisibility(area.id, "fallback");

        continue;
      }

      await this.renderElement(area, element, this.App);

      this.setAreaContainersVisibility(area.id, "primary");
    }
  }

  async renderWidgets() {
    const widgets = this.Scene.model.widgets ?? [];

    await this.WidgetsFactory.renderWidgets(widgets);
  }

  async renderElements() {
    const elements = this.Scene.model.elements ?? [];

    await this.ElementsFactory.renderElements(elements);
  }

  async renderAlerts() {
    await this.AlertsFactory.renderAlerts();
  }

  async renderOverlays() {
    const areas = this.Scene.model.areas ?? [];

    await this.OverlaysFactory.renderOverlays(areas);

    // Los overlays de área también devuelven updaters (partículas, procedural,
    // animaciones): el mismo ticker del mask los corre por frame.
    this.ensureMaskAnimTicker();
  }

  async renderPanels() {
    const areas = this.Scene.model.areas ?? [];

    await this.PanelsFactory.renderPanels(areas);

    this.ensureMaskAnimTicker();
  }

  private async renderFallback(
    area: IStudioArea,

    _application: Application,
  ) {
    const fallbackKey = `fallback_${area.id}`;

    const boundArea = {
      bound: area.bounds,

      container: {
        width: this.Options.width,

        height: this.Options.height,
      },
    };

    const sizeArea = StrategyBounds.getBounds(boundArea);

    const destination = {
      x: sizeArea.left,

      y: sizeArea.top,

      width: sizeArea.width,

      height: sizeArea.height,
    };

    const dimensionsText = `${Math.round(destination.width)} × ${Math.round(destination.height)}`;

    let fallbackContainer = this.DrawCache.Containers[fallbackKey];

    if (!fallbackContainer) {
      fallbackContainer = new Container();

      this.DrawCache.Containers[fallbackKey] = fallbackContainer;

      this.Root.addChild(fallbackContainer);
    } else {
      fallbackContainer.removeChildren();
    }

    const isRemoteDevice =
      area.media.resource?.type === ITypeMedia.remoteDevice;

    const background = new Graphics();

    background.roundPixels = true;

    background

      .rect(0, 0, destination.width, destination.height)

      .fill({
        color: isRemoteDevice ? 0x000000 : 0x303030,

        alpha: isRemoteDevice ? 0 : 0.75,
      });

    fallbackContainer.zIndex = area.bounds.zIndex;

    fallbackContainer.addChild(background);

    fallbackContainer.position.set(destination.x, destination.y);

    fallbackContainer.label = fallbackKey;

    if (!isRemoteDevice) {
      const fontSize = Math.min(
        destination.width / 10,

        destination.height / 8,

        24 * 1.5,
      );

      const text = new Text({
        text: dimensionsText,

        style: {
          fontFamily: "Roboto",

          fontSize: fontSize,

          fill: "#ffffff",

          align: "center",
        },
      });

      text.anchor.set(0.5);

      text.position.set(destination.width / 2, destination.height / 2);

      fallbackContainer.addChild(text as unknown as ContainerChild);

      this.DrawCache.Elements[fallbackKey] = {
        element: text as unknown as HTMLVideoElement | HTMLImageElement,

        type: ITypeMedia.display,
      };
    }

    // Fallback is fully static — cache as texture so PixiJS skips re-rendering each tick

    fallbackContainer.cacheAsTexture(true);
  }

  private async renderElement(
    area: IStudioArea,

    element: HTMLVideoElement | HTMLImageElement | HTMLCanvasElement,

    _application: Application,
  ) {
    const boundArea = {
      bound: area.bounds,

      container: {
        width: this.Options.width,

        height: this.Options.height,
      },
    };

    const sizeArea = StrategyBounds.getBounds(boundArea);

    const boundMedia = {
      bound: area.media.bounds,

      container: {
        top: sizeArea.top,

        left: sizeArea.left,

        width: sizeArea.width,

        height: sizeArea.height,
      },
    };

    const sizeMedia = StrategyBounds.getBounds(boundMedia);

    if (!area.media.resource) return;

    const isVideo = element instanceof HTMLVideoElement;

    const type = area.media.resource.type;

    if (isVideo) {
      try {
        if (type === ITypeMedia.video || type === ITypeMedia.videoExternal) {
          const settings = area.media.resource.settings;

          if (settings && settings.timeline !== undefined) {
            const last = this.DrawCache.LastTimeline[area.id];

            if (last !== settings.timeline) {
              element.currentTime = settings.timeline;

              await new Promise<void>((resolve) => {
                const onSeeked = () => {
                  element.removeEventListener("seeked", onSeeked);

                  resolve();
                };

                element.addEventListener("seeked", onSeeked);
              });

              this.DrawCache.LastTimeline[area.id] = settings.timeline;
            }
          }

          if (element.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
            try {
              if (settings && settings.playing && element.paused) {
                await element.play();
              }

              if (settings && !settings.playing && !element.paused) {
                await element.pause();
              }
            } catch (playError) {
              console.warn("Video play warning:", playError);
            }
          }
        }
      } catch (playError) {
        console.warn("Video play warning:", playError);
      }
    }

    const destination = {
      x: sizeArea.left,

      y: sizeArea.top,

      width: sizeArea.width,

      height: sizeArea.height,
    };

    const source = {
      x: sizeMedia.left,

      y: sizeMedia.top,

      width: sizeMedia.width,

      height: sizeMedia.height,
    };

    this.DrawCache.positions[area.id] = { destination, source };

    // #4 Cache de capas estáticas: si el layout (destino/fuente/zIndex), los filtros, el tipo y el

    // elemento subyacente no cambiaron, reusamos el sprite/máscara/container ya construidos en vez

    // de recrearlos cada frame (new Sprite + new Graphics + removeChildren + addChild → GC churn a

    // 60fps por área, incluso para una imagen quieta). Solo refrescamos las texturas DINÁMICAS:

    //   - canvas (display capture): source.update() (no se auto-actualiza).

    //   - video: la VideoSource de Pixi se auto-actualiza sola (no hace falta tocar nada).

    //   - imagen: estática → nada.

    // Corre también en el OSR (broadcast), así que la firma debe capturar TODO lo visual.

    const renderSig = JSON.stringify({
      d: destination,

      s: source,

      z: area.bounds.zIndex,

      f: area.media.filters ?? [],

      t: type,

      // Dims nativas del video: cuando pasan de 0 (no cargado) a reales, cambia

      // la firma → se re-renderiza y se aplica el cover con el aspecto correcto.

      v:
        isVideo && element instanceof HTMLVideoElement
          ? `${element.videoWidth}x${element.videoHeight}`
          : "",
    });

    const cachedSprite = this.DrawCache.Sprites[area.id];

    const cachedContainer = this.DrawCache.Containers[area.id];

    const sameElement = this.DrawCache.Elements[area.id]?.element === element;

    if (
      renderSig === this.DrawCache.AreaSignatures[area.id] &&
      cachedSprite &&
      !cachedSprite.destroyed &&
      cachedContainer &&
      !cachedContainer.destroyed &&
      sameElement
    ) {
      if (element instanceof HTMLCanvasElement) {
        this.DrawCache.Texture[area.id]?.source.update();
      }

      return;
    }

    this.DrawCache.AreaSignatures[area.id] = renderSig;

    let texture: Texture;

    let sprite: Sprite;

    {
      texture = Texture.from(element);

      sprite = new Sprite(texture);

      // Canvas elements transferred to OffscreenCanvas cannot be resized

      if (element instanceof HTMLCanvasElement) {
        // Pixi caches canvas textures statically — force update each frame

        texture.source.update();
      } else {
        element.width = destination.width;

        element.height = destination.height;
      }
    }

    if (sprite) {
      sprite.anchor.set(0, 0);

      // Cover-fit para cámara/video: en vez de estirar el video a source.width/

      // height (fill → cara achatada), lo escalamos a su aspecto NATIVO para que

      // LLENE el área (max ratio) y lo centramos; la máscara del container (rect =

      // bounds del área) recorta el sobrante. Replica el object-fit: cover del DOM

      // (assembler). Conservamos source.x/y como base y sólo desplazamos por la

      // mitad del excedente → no toca el sistema de coordenadas existente.

      const shouldCover =
        type === ITypeMedia.camera ||
        type === ITypeMedia.video ||
        type === ITypeMedia.videoExternal ||
        type === ITypeMedia.remoteDevice;

      const texW = texture.width;

      const texH = texture.height;

      if (shouldCover && texW > 0 && texH > 0) {
        const coverScale = Math.max(source.width / texW, source.height / texH);

        const drawW = texW * coverScale;

        const drawH = texH * coverScale;

        sprite.width = drawW;

        sprite.height = drawH;

        sprite.x = source.x - (drawW - source.width) / 2;

        sprite.y = source.y - (drawH - source.height) / 2;
      } else {
        sprite.x = source.x;

        sprite.y = source.y;

        sprite.width = source.width;

        sprite.height = source.height;
      }

      let container = this.DrawCache.Containers[area.id];

      if (!container) {
        container = new Container();

        this.DrawCache.Containers[area.id] = container;

        this.Root.addChild(container);
      } else {
        container.removeChildren();
      }

      container.width = destination.width;

      container.height = destination.height;

      container.zIndex = area.bounds.zIndex;

      container.position.set(destination.x, destination.y);

      container.addChild(sprite);

      this.renderFilter(area, sprite);

      let mask = this.DrawCache.Masks[area.id];

      if (!mask) {
        mask = new Graphics();

        this.DrawCache.Masks[area.id] = mask;
      }

      mask.clear();

      mask.rect(0, 0, destination.width, destination.height).fill(0xffffff);

      container.mask = mask;

      if (!container.children.includes(mask)) {
        container.addChild(mask);
      }

      this.Root.sortDirty = true;
    }

    this.DrawCache.Texture[area.id] = texture;

    this.DrawCache.Sprites[area.id] = sprite;

    if (area.media.resource?.type) {
      this.DrawCache.Elements[area.id] = {
        element,

        type: area.media.resource.type,
      };
    }
  }

  private renderFilter(area: IStudioArea, sprite: Sprite) {
    try {
      const definitions = (area.media.filters ?? [])

        .slice()

        .sort((a, b) => a.order - b.order);

      const signature = JSON.stringify(
        definitions.map((d) => ({
          name: d.name,

          order: d.order,

          options: d.options,
        })),
      );

      const prevSig = this.DrawCache.FilterSignatures[area.id];

      const cached = this.DrawCache.Filters[area.id];

      if (prevSig === signature && cached) {
        sprite.filters = cached.length > 0 ? cached : [];

        const pos = this.DrawCache.positions[area.id];

        if (cached.length > 0 && pos) {
          sprite.filterArea = new Rectangle(
            0,

            0,

            pos.destination.width,

            pos.destination.height,
          );
        }

        return;
      }

      const pixiFilters: Filter[] = definitions

        .map((def) => {
          const kind = def.name as FilterKind;

          const defaultVal =
            AVAILABLE.find((a) => a.key === kind)?.default ?? 1;

          const value =
            typeof def.options?.value === "number"
              ? def.options.value
              : defaultVal;

          const params = (def.options?.params ?? undefined) as
            | Record<string, number>
            | undefined;

          return createPixiFilter(kind, value, params);
        })

        .filter((f): f is Filter => !!f);

      sprite.filters = pixiFilters.length > 0 ? pixiFilters : [];

      // Set fixed filterArea to avoid bounds recalculation each tick.

      // Video sprites invalidate bounds cache every frame (rVFC marks source dirty).

      const pos = this.DrawCache.positions[area.id];

      if (pixiFilters.length > 0 && pos) {
        sprite.filterArea = new Rectangle(
          0,

          0,

          pos.destination.width,

          pos.destination.height,
        );
      } else {
        sprite.filterArea = undefined;
      }

      this.DrawCache.Filters[area.id] = pixiFilters;

      this.DrawCache.FilterSignatures[area.id] = signature;
    } catch (e) {
      console.warn(
        "No se pudieron aplicar filtros; se continúa sin filtros.",

        e,
      );

      sprite.filters = [];
    }
  }

  async drawMask() {
    try {
      const model = this.Scene.model;

      const mask = model.mask;

      if (!mask) {
        this.MaskAnim = null;

        // Sin mask puede no quedar ningún updater: retira el ticker si sobra.
        this.ensureMaskAnimTicker();

        return this;
      }

      const maskKey = "global_mask";

      // NUEVO: el mask como composición de overlay full-canvas (sin área).

      if (mask.type === MaskType.composition) {
        return this.drawMaskComposition(mask, maskKey);
      }

      // --- external (legacy): un único asset de video full-canvas ---

      let maskContainer = this.DrawCache.Containers[maskKey];

      if (!maskContainer) {
        const videoElement = document.createElement("video");

        videoElement.src = mask.src;

        videoElement.autoplay = true;

        videoElement.loop = true;

        videoElement.crossOrigin = "anonymous";

        videoElement.width = this.Options.width;

        videoElement.height = this.Options.height;

        try {
          await videoElement.play();
        } catch (playError) {
          console.warn("Video play warning:", playError);
        }

        const texture = Texture.from(videoElement);

        const sprite = new Sprite(texture);

        sprite.width = this.Options.width;

        sprite.height = this.Options.height;

        sprite.x = 0;

        sprite.y = 0;

        maskContainer = new Container();

        maskContainer.width = this.Options.width;

        maskContainer.height = this.Options.height;

        maskContainer.x = 0;

        maskContainer.y = 0;

        maskContainer.addChild(sprite);

        this.DrawCache.Containers[maskKey] = maskContainer;

        this.DrawCache.Texture[maskKey] = texture;

        this.DrawCache.Sprites[maskKey] = sprite;

        this.Root.addChild(maskContainer);
      }
    } catch (error) {
      console.error("Error in drawMask:", error);
    }

    return this;
  }

  /**
   * Renderiza un mask de tipo `composition` reusando el mismo CustomOverlayRenderer
   * que `area.overlay`, pero con rect = lienzo completo (o `mask.bounds`) y sin
   * depender de ningún área. Es OverlaysFactory con el rect del lienzo en vez de
   * area.bounds. Corre cada frame: reconstruimos sólo si cambió la firma del mask.
   */

  private drawMaskComposition(mask: MaskComposition, maskKey: string) {
    const signature = JSON.stringify(mask);

    const cached = this.DrawCache.Containers[maskKey];

    const haveCached = !!cached && !cached.destroyed;

    // Sin cambios → no reconstruir (la composición ya está montada en el stage).

    if (haveCached && this.DrawCache.AreaSignatures[maskKey] === signature) {
      return this;
    }

    let maskContainer: Container;

    if (haveCached && cached) {
      maskContainer = cached;

      for (const child of maskContainer.removeChildren()) {
        child.destroy({ children: true });
      }

      maskContainer.mask = null;
    } else {
      maskContainer = new Container();

      maskContainer.label = maskKey;

      this.DrawCache.Containers[maskKey] = maskContainer;

      this.Root.addChild(maskContainer);
    }

    const rect = StrategyBounds.getBounds({
      bound: {
        left: mask.bounds?.left ?? 0,

        top: mask.bounds?.top ?? 0,

        width: mask.bounds?.width ?? 100,

        height: mask.bounds?.height ?? 100,
      },

      container: {
        width: this.Options.width,

        height: this.Options.height,
      },
    });

    maskContainer.position.set(rect.left, rect.top);

    // La máscara (chrome de la escena) va 1 z POR ENCIMA de toda la escena

    // (áreas/cámaras + sus overlays z+0.5 / panels z+0.75), pero POR DEBAJO de

    // los widgets/elements (z10 por defecto) y los alerts (z100). maxAreaZ+1

    // supera el +0.75 del panel y queda holgado bajo el 10. Si la mask trae

    // zIndex explícito, se respeta (ej. HUD por encima de todo).

    const maxAreaZ = (this.Scene.model.areas ?? []).reduce(
      (max, area) => Math.max(max, area?.bounds?.zIndex ?? 0),

      0,
    );

    maskContainer.zIndex = mask.zIndex ?? maxAreaZ + 1;

    // Lista viva de updaters + reloj reiniciado (las entry-anims se reproducen
    // en cada rebuild). Se instala ANTES del render: las registraciones tardías
    // (typewriter tras cargar la fuente) caen acá y re-registran el ticker.

    const updaters = new UpdaterList();

    updaters.onPush = () => this.ensureMaskAnimTicker();

    this.MaskAnim = { updaters, startMs: Date.now() };

    this.MaskOverlayRenderer.render(
      mask.overlay,

      maskContainer,

      {
        width: rect.width,

        height: rect.height,
      },

      undefined,

      updaters,
    );

    this.ensureMaskAnimTicker();

    this.DrawCache.AreaSignatures[maskKey] = signature;

    this.Root.sortDirty = true;

    return this;
  }

  /**
   * Registra (una sola vez) el ticker que anima por frame los elementos del mask,
   * de los overlays de área y de los panels; lo retira cuando no queda ningún
   * updater. Se remueve también en clear().
   */

  /** ¿Hay algún updater per-frame vivo (mask, overlays de área o panels)? */

  private hasAnimUpdaters(): boolean {
    return this.animStates.some(
      (state) =>
        !!state && state.updaters.some((update) => !this.DoneUpdaters.has(update)),
    );
  }

  private ensureMaskAnimTicker() {
    // Sin updaters no hay nada que correr: se retira el callback (si estaba)
    // para no pagar un ticker por frame en escenas sin mask ni efectos.
    if (!this.hasAnimUpdaters()) {
      this.removeAnimTicker();

      return;
    }

    if (!this.MaskAnimTicker) {
      this.MaskAnimTicker = () => this.stepAnim();
    }

    // Una capa pausada u oculta (pool, build offscreen de un pase, stinger) no
    // paga el ticker: lo registran resume() / setVisible(true) al mostrarla.
    const ticker = this.App.ticker;

    if (ticker && !this.MaskTickerActive && !this.Paused && this.Root.visible) {
      ticker.add(this.MaskAnimTicker);

      this.MaskTickerActive = true;
    }
  }

  private get animStates() {
    return [this.MaskAnim, this.OverlaysFactory.Anim, this.PanelsFactory.Anim];
  }

  /**
   * Corre UN frame de todos los updaters (mask, overlays de área, panels) con
   * el reloj real. Un updater que devuelve `true` terminó (entry-anim finita en
   * su estado final, typewriter completo): queda marcado y no se vuelve a
   * llamar; cuando no queda ninguno activo se retira el ticker. Los updaters
   * se conservan: al volver a mostrar la capa se reinician (setVisible).
   */

  stepAnim(): void {
    const now = Date.now();

    let remaining = 0;

    for (const state of this.animStates) {
      if (!state || state.updaters.length === 0) continue;

      const elapsed = now - state.startMs;

      for (const update of state.updaters) {
        if (this.DoneUpdaters.has(update)) continue;

        if (update(elapsed, state.startMs) === true) {
          this.DoneUpdaters.add(update);
        } else {
          remaining += 1;
        }
      }
    }

    if (remaining === 0) this.removeAnimTicker();
  }

  /**
   * Precalienta los updaters con un reloj sintético (frames a 60 Hz) sin
   * descartar ninguno: para el snapshot de una escena oculta/pausada, cuyos
   * canvas auxiliares (partículas/procedural) solo se pintan dentro del updater
   * y cuyos emisores de baja tasa (nubes, estrellas) no tendrían nada en un
   * único frame. No toca el reloj: al mostrar la capa, setVisible(true) lo
   * reinicia y las entry-anims se reproducen igual.
   */

  warmAnim(frames = 90): void {
    const frameMs = 1000 / 60;

    for (const state of this.animStates) {
      if (!state || state.updaters.length === 0) continue;

      for (let i = 1; i <= frames; i += 1) {
        for (const update of state.updaters) {
          update(i * frameMs, state.startMs);
        }
      }
    }
  }

  updateOnLive(onLive: boolean) {
    this.State.onLive = onLive;

    if (this.State.onLive && this.ThumbnailWorker) {
      this.ThumbnailWorker.terminate();

      if (this.ScreenshotTimer) clearTimeout(this.ScreenshotTimer);
    } else if (!this.ThumbnailWorker) {
      this.createWorker();
    }
  }

  async switchScene(scene: ISceneDB, isStreaming: boolean) {
    this.clear();

    this.App.renderer.background.color = 0x000000;

    this.DrawCache = new DrawCache();

    this.Scene = scene;

    this.BackgroundImageRenderer.invalidateCache();

    this.ThumbnailQueueFree = true;

    this.updateOnLive(isStreaming);
  }

  private lastModelSig: string | null = null;

  private sigCounter = 0;

  /** Stable-ish signature of the full model, used to skip redundant rebuilds.
   *  Mirrors the FilterSignatures memoization already used in renderFilter(). */

  private computeModelSignature(model: IStudio): string {
    try {
      return JSON.stringify(model);
    } catch {
      // Non-serializable model — return a unique value so it never matches

      // and the rebuild always runs (safe fallback).

      return `nosig-${++this.sigCounter}`;
    }
  }

  async updateModel(model: IStudio, _isStreaming: boolean) {
    // Skip the full GPU teardown + rebuild when the incoming model is

    // identical to the one already rendered — redundant updateModel calls

    // happen on no-op editor interactions. clear() resets lastModelSig, so

    // any real teardown (switchScene/destroy) forces the next call to rebuild.

    const signature = this.computeModelSignature(model);

    if (signature === this.lastModelSig) {
      this.Scene.model = model;

      return;
    }

    // Camino rápido: si SOLO cambió el layout de áreas que ya existían (bounds, encuadre, filtros,
    // visibilidad) no hace falta tirar y reconstruir la escena entera (widgets, elementos, alertas,
    // overlays, paneles, máscara): renderAreas corre cada frame con una firma por área y re-arma
    // solo el sprite/container del área que cambió. Es el caso de cada drag/resize en el editor,
    // que en el OSR se aplicaba con un rebuild completo a resolución de emisión.
    if (this.lastModelSig !== null && isAreaLayoutOnlyChange(this.Scene.model, model)) {
      this.Scene.model = model;

      this.lastModelSig = signature;

      return;
    }

    this.clear();

    this.Scene.model = model;

    this.DrawCache = new DrawCache();

    this.BackgroundImageRenderer.invalidateCache();

    this.State.isDrawing = true;

    this.lastModelSig = signature;
  }

  clear() {
    this.lastModelSig = null;

    this.State.isDrawing = false;

    // Remove the mask animation ticker so it doesn't keep firing / leaking

    // after teardown (mirrors AlertsFactory.destroy's ticker removal).

    if (this.MaskAnimTicker) {
      this.App.ticker?.remove(this.MaskAnimTicker);

      this.MaskAnimTicker = null;
    }

    this.MaskTickerActive = false;

    this.MaskAnim = null;

    // Clean up widgets first (timers, service subscriptions, etc.)

    this.WidgetsFactory.destroy();

    this.ElementsFactory.destroy();

    this.AlertsFactory.destroy();

    this.OverlaysFactory.destroy();

    this.PanelsFactory.destroy();

    // global_mask: el subárbol del CustomOverlayRenderer (partículas/procedural
    // con textura propia, textos, formas) se destruye con children:true para
    // liberar canvas y texturas; el teardown genérico de abajo solo vacía el
    // container. Las texturas de Assets no se tocan (destroy() por defecto).
    const globalMask = this.DrawCache.Containers.global_mask;

    if (globalMask && !globalMask.destroyed) {
      for (const child of globalMask.removeChildren()) {
        child.destroy({ children: true });
      }
    }

    // Unload sprites to release GPU resources (PixiJS 8.15.0+)

    for (const key in this.DrawCache.Sprites) {
      const sprite = this.DrawCache.Sprites[key];

      if (sprite && !sprite.destroyed) {
        if (sprite.filters) {
          const filters = sprite.filters;

          sprite.filters = [];

          for (const filter of filters) {
            if (filter && typeof filter.destroy === "function") {
              try {
                filter.destroy();
              } catch (e) {
                console.warn("Error destroying filter:", e);
              }
            }
          }
        }

        // Use unload() to release GPU resources without destroying the object

        if (typeof sprite.unload === "function") {
          sprite.unload();
        }

        sprite.destroy({ children: true, texture: false });
      }
    }

    for (const key in this.DrawCache.Containers) {
      const container = this.DrawCache.Containers[key];

      if (container && !container.destroyed) {
        container.removeChildren();

        try {
          container.destroy();
        } catch (e) {
          console.warn("Error destroying container:", e);
        }
      }
    }

    // Unload textures to release GPU resources

    for (const key in this.DrawCache.Texture) {
      const texture = this.DrawCache.Texture[key];

      if (texture && !texture.destroyed) {
        // Unload texture source from GPU before disposing

        if (texture.source && typeof texture.source.unload === "function") {
          texture.source.unload();
        }

        texture.source?.resource?.dispose?.();
      }
    }

    for (const key in this.DrawCache.Masks) {
      const mask = this.DrawCache.Masks[key];

      if (mask && !mask.destroyed) {
        mask.destroy({ children: true });
      }
    }

    // Clean up widgets

    for (const key in this.DrawCache.Containers) {
      if (key.startsWith("widget_")) {
        const container = this.DrawCache.Containers[key];

        if (container && !container.destroyed) {
          container.removeChildren();

          try {
            container.destroy();
          } catch (e) {
            console.warn("Error destroying widget container:", e);
          }
        }
      }
    }

    // Clean up elements

    for (const key in this.DrawCache.Containers) {
      if (key.startsWith("element_")) {
        const container = this.DrawCache.Containers[key];

        if (container && !container.destroyed) {
          container.removeChildren();

          try {
            container.destroy();
          } catch (e) {
            console.warn("Error destroying element container:", e);
          }
        }
      }
    }

    if (this.TestPattern && !this.TestPattern.destroyed) {
      this.TestPattern.destroy({ children: true });

      this.TestPattern = undefined;
    }

    if (this.BgColor && !this.BgColor.destroyed) {
      this.BgColor.destroy();
    }

    this.BgColor = undefined;

    this.BackgroundImageRenderer.clear();

    this.DrawCache = new DrawCache();

    this.ThumbnailQueueFree = true;
  }

  isScreenshotTime() {
    return this.ThumbnailQueueFree;
  }

  private captureScreenshot() {
    if (this.State.onLive && this.ScreenshotTimer) {
      clearTimeout(this.ScreenshotTimer);

      return Promise.resolve();
    }

    const rendererCanvas = document.querySelector(
      "#canvas-studio",
    ) as HTMLCanvasElement;

    if (!rendererCanvas) {
      return Promise.resolve();
    }

    return createImageBitmap(rendererCanvas, {
      resizeWidth: this.Options.width,

      resizeHeight: this.Options.height,

      resizeQuality: "high",
    }).then((bitmap) => {
      const id = this.Scene.model.id;

      const name = `${id}.jpg`;

      return new Promise((resolve) => {
        this.ThumbnailWorker.onmessage = () => {
          resolve(true);
        };

        this.ThumbnailWorker.postMessage({ type: "frame", bitmap, name, id }, [
          bitmap,
        ]);
      });
    });
  }

  async screenshot() {
    await this.captureScreenshot();

    if (this.ScreenshotTimer) {
      clearTimeout(this.ScreenshotTimer);
    }

    //this.ScreenshotTimer = setTimeout(() => {

    //  this.screenshot();

    //}, 1000);
  }

  stop() {
    this.State.onLive = false;

    this.State.isDrawing = false;
  }

  deleteArea(id: string) {
    // Clean up browser capture resources (worker + Kero connection + stream)

    const cached = this.DrawCache.Elements[id];

    if (cached?.type === ITypeMedia.browser) {
      const captureIds = new Set<string>();

      const managerCaptureId = BrowserPreviewManager.getCaptureId(id);

      if (managerCaptureId) {
        captureIds.add(managerCaptureId);
      }

      // Fallback: close by captureId stored in scene model if manager mapping is stale/missing.

      const sceneArea = this.Scene.model.areas.find((area) => area.id === id);

      const resourceCaptureId =
        sceneArea?.media?.resource?.type === ITypeMedia.browser
          ? sceneArea.media.resource.captureId
          : undefined;

      if (resourceCaptureId) {
        captureIds.add(resourceCaptureId);
      }

      for (const captureId of captureIds) {
        browserCaptureService.close(captureId).catch(console.error);
      }

      BrowserPreviewManager.destroy(id);
    }

    const sprite = this.DrawCache.Sprites[id];

    if (sprite && !sprite.destroyed) {
      if (sprite.filters) {
        const filters = sprite.filters;

        sprite.filters = [];

        for (const filter of filters) {
          if (filter && typeof filter.destroy === "function") {
            try {
              filter.destroy();
            } catch (e) {
              console.warn("Error destroying filter:", e);
            }
          }
        }
      }

      // Use unload() to release GPU resources before destroying

      if (typeof sprite.unload === "function") {
        sprite.unload();
      }

      sprite.destroy({ children: true, texture: false });
    }

    delete this.DrawCache.Sprites[id];

    const container = this.DrawCache.Containers[id];

    if (container && !container.destroyed) {
      // Bug Pixi.js v8: removeChildren() sin destruir, luego destroy() del container vacío

      container.removeChildren();

      container.destroy();
    }

    delete this.DrawCache.Containers[id];

    const texture = this.DrawCache.Texture[id];

    if (texture && !texture.destroyed) {
      // Unload texture source from GPU before disposing

      if (texture.source && typeof texture.source.unload === "function") {
        texture.source.unload();
      }

      texture.source?.resource?.dispose?.();
    }

    delete this.DrawCache.Texture[id];

    delete this.DrawCache.Filters[id];

    delete this.DrawCache.FilterSignatures[id];

    delete this.DrawCache.AreaSignatures[id];

    delete this.DrawCache.positions[id];

    delete this.DrawCache.Elements[id];

    const fallbackKey = `fallback_${id}`;

    const fallbackContainer = this.DrawCache.Containers[fallbackKey];

    if (fallbackContainer && !fallbackContainer.destroyed) {
      // Bug Pixi.js v8: removeChildren() sin destruir, luego destroy() del container vacío

      fallbackContainer.removeChildren();

      fallbackContainer.destroy();
    }

    delete this.DrawCache.Containers[fallbackKey];

    delete this.DrawCache.Elements[fallbackKey];
  }

  destroy() {
    this.State.onLive = false;

    this.clear();

    this.State.isDrawing = false;

    // Saca y destruye el Root de la escena (evicción del pool / teardown total).

    if (this.Root && !this.Root.destroyed) {
      this.Root.removeFromParent();

      this.Root.destroy({ children: true });
    }

    this.ThumbnailWorker.terminate();

    this.ThumbnailQueueFree = true;
  }
}

export default RenderFactory;
